import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api, type Node } from '../lib/api'
import { Button, Table, Modal, Field, Input, Select, Badge, Checkbox, ErrorBox } from '../components/ui'
import { IconPlus } from '../components/icons'
import { RowMenu } from '../components/RowMenu'
import { FilterBar, FilterSelect } from '../components/Filters'

interface IPAddress {
  id: number
  node_id: string
  ip: string
  role: string
  comment?: string
  is_default: boolean
  created_at: string
}

type IPTab = 'addresses' | 'pools' | 'nat'

export function IPAddressesPage() {
  const [tab, setTab] = useState<IPTab>('addresses')
  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">IP-адреса</h1>
        <p className="text-[13px] text-neutral-500 mt-1">Адреса нод, пулы подсетей и NAT-правила.</p>
      </div>
      <div className="flex gap-1 mb-5 border-b border-neutral-200">
        {([
          ['addresses', 'Адреса'],
          ['pools',     'Пулы IP'],
          ['nat',       'NAT'],
        ] as [IPTab, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3.5 py-2 text-[13px] font-medium border-b-2 -mb-px transition ${
              tab === id ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'
            }`}>{label}</button>
        ))}
      </div>
      {tab === 'addresses' && <AddressesTab />}
      {tab === 'pools'     && <PoolsTab />}
      {tab === 'nat'       && <NATTab />}
    </>
  )
}

function AddressesTab() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [fNode, setFNode] = useState('')

  const { data: nodes = [] } = useQuery({
    queryKey: ['nodes'],
    queryFn: async () => (await api.get<Node[]>('/nodes')).data,
  })
  const { data: ips = [] } = useQuery({
    queryKey: ['ip-addresses'],
    queryFn: async () => (await api.get<IPAddress[]>('/ip-addresses')).data,
  })
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n.name]))
  const filtered = useMemo(() => fNode ? ips.filter(x => x.node_id === fNode) : ips, [ips, fNode])

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/ip-addresses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ip-addresses'] }),
  })
  const setDefault = useMutation({
    mutationFn: async (id: number) => api.post(`/ip-addresses/${id}/default`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ip-addresses'] }),
  })
  const discover = useMutation({
    mutationFn: async (nid: string) => (await api.post(`/nodes/${nid}/ip-addresses/discover`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ip-addresses'] }),
  })

  return (
    <>
      <div className="flex justify-end gap-2 mb-4">
        {fNode && (
          <Button variant="secondary" onClick={() => discover.mutate(fNode)} disabled={discover.isPending}>
            {discover.isPending ? 'Сканируем…' : 'Пересканировать'}
          </Button>
        )}
        <Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить IP</Button>
      </div>
      <FilterBar>
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
      </FilterBar>

      <Table<IPAddress>
        rows={filtered}
        empty={ips.length === 0 ? 'Нет IP-адресов. Добавьте ноду — обнаружатся автоматически.' : 'Ничего не найдено.'}
        cols={[
          { key: 'ip', label: 'IP-адрес', render: (x) => (
            <span className="font-mono font-medium flex items-center gap-2">
              {x.ip}
              {x.is_default && <Badge tone="blue">по умолчанию</Badge>}
            </span>
          )},
          { key: 'nd', label: 'Нода', render: (x) => <span className="text-[13px] text-neutral-700">{nodeMap[x.node_id] || x.node_id.slice(0, 8)}</span> },
          { key: 'r', label: 'Роль', render: (x) => (
            <Badge tone={x.role === 'dedicated' ? 'purple' : 'neutral'}>{x.role === 'dedicated' ? 'выделенный' : 'общий'}</Badge>
          )},
          { key: 'c', label: 'Комментарий', render: (x) => <span className="text-[12px] text-neutral-500">{x.comment || '—'}</span> },
          { key: 'act', label: '', w: '1%', render: (x) => (
            <div className="flex justify-end">
              <RowMenu items={[
                ...(x.is_default ? [] : [{ label: 'Сделать по умолчанию', onClick: () => setDefault.mutate(x.id) }]),
                { divider: true, label: '', onClick: () => {} },
                { label: 'Удалить', danger: true,
                  onClick: () => confirm(`Удалить IP ${x.ip}?`) && del.mutate(x.id) },
              ]} />
            </div>
          )},
        ]}
      />
      {addOpen && <AddIPModal nodes={nodes} onClose={() => setAddOpen(false)}
        onDone={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['ip-addresses'] }) }} />}
    </>
  )
}

function AddIPModal({ nodes, onClose, onDone }: { nodes: Node[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ node_id: nodes[0]?.id || '', ip: '', role: 'shared', comment: '', is_default: false })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/ip-addresses', f)).data,
    onSuccess: onDone,
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Добавить IP-адрес">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Нода">
          <Select value={f.node_id} onChange={e => setF({ ...f, node_id: e.target.value })}>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.name} — {n.ip_address}</option>)}
          </Select>
        </Field>
        <Field label="IP-адрес">
          <Input value={f.ip} onChange={e => setF({ ...f, ip: e.target.value })} placeholder="192.168.1.10 или 2a0e:e6c0::1" required autoFocus />
        </Field>
        <Field label="Роль">
          <Select value={f.role} onChange={e => setF({ ...f, role: e.target.value })}>
            <option value="shared">Общий (для нескольких сайтов)</option>
            <option value="dedicated">Выделенный (один сайт)</option>
          </Select>
        </Field>
        <Field label="Комментарий (опционально)">
          <Input value={f.comment} onChange={e => setF({ ...f, comment: e.target.value })} />
        </Field>
        <Checkbox label="Сделать этот IP по умолчанию для ноды"
                  checked={f.is_default} onChange={e => setF({ ...f, is_default: e.target.checked })} />
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Создаём…' : 'Создать'}</Button>
        </div>
      </form>
    </Modal>
  )
}

// =================== ПУЛЫ IP ===================

interface IPPool {
  id: number; node_id: string; name: string; cidr: string
  comment?: string; created_at: string
}

function PoolsTab() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const { data: pools = [] } = useQuery({ queryKey: ['ip-pools'], queryFn: async () => (await api.get<IPPool[]>('/ip-pools')).data })
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n.name]))
  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/ip-pools/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ip-pools'] }),
  })
  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить пул</Button>
      </div>
      <Table<IPPool>
        rows={pools}
        empty="Нет пулов. Пул — это подсеть IP, из которой выделяются адреса сайтам."
        cols={[
          { key: 'name', label: 'Имя', render: (p) => <span className="font-medium">{p.name}</span> },
          { key: 'cidr', label: 'CIDR', render: (p) => <span className="font-mono text-[12px]">{p.cidr}</span> },
          { key: 'nd', label: 'Нода', render: (p) => <span className="text-[13px] text-neutral-700">{nodeMap[p.node_id] || p.node_id.slice(0, 8)}</span> },
          { key: 'c', label: 'Комментарий', render: (p) => <span className="text-[12px] text-neutral-500">{p.comment || '—'}</span> },
          { key: 'act', label: '', w: '1%', render: (p) => (
            <div className="flex justify-end">
              <RowMenu items={[
                { label: 'Удалить', danger: true,
                  onClick: () => confirm(`Удалить пул ${p.name}?`) && del.mutate(p.id) },
              ]} />
            </div>
          )},
        ]}
      />
      {addOpen && <AddPoolModal nodes={nodes} onClose={() => setAddOpen(false)}
        onDone={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['ip-pools'] }) }} />}
    </>
  )
}

function AddPoolModal({ nodes, onClose, onDone }: { nodes: Node[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ node_id: nodes[0]?.id || '', name: '', cidr: '', comment: '' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/ip-pools', f)).data,
    onSuccess: onDone,
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Добавить пул IP">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Нода">
          <Select value={f.node_id} onChange={e => setF({ ...f, node_id: e.target.value })}>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
          </Select>
        </Field>
        <Field label="Имя пула"><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="main-pool" required autoFocus /></Field>
        <Field label="CIDR" hint="Пример: 46.8.189.0/24 или 2a0e:e6c0::/64">
          <Input value={f.cidr} onChange={e => setF({ ...f, cidr: e.target.value })} placeholder="10.0.0.0/24" required className="font-mono" />
        </Field>
        <Field label="Комментарий"><Input value={f.comment} onChange={e => setF({ ...f, comment: e.target.value })} /></Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-100 -mx-5 px-5 pt-4">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>{create.isPending ? '…' : 'Создать'}</Button>
        </div>
      </form>
    </Modal>
  )
}

// =================== NAT ===================

interface NATRule {
  id: number; node_id: string; external: string; internal: string
  comment?: string; created_at: string
}

function NATTab() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const { data: rules = [] } = useQuery({ queryKey: ['nat-rules'], queryFn: async () => (await api.get<NATRule[]>('/nat-rules')).data })
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n.name]))
  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/nat-rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nat-rules'] }),
  })
  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить NAT-правило</Button>
      </div>
      <Table<NATRule>
        rows={rules}
        empty="Нет NAT-правил. NAT сопоставляет внешний IP внутреннему (для сайтов в приватной сети)."
        cols={[
          { key: 'ext', label: 'Внешний IP', render: (r) => <span className="font-mono text-[13px] font-medium">{r.external}</span> },
          { key: 'arr', label: '', render: () => <span className="text-neutral-400">→</span>, w: '1%' },
          { key: 'int', label: 'Внутренний IP', render: (r) => <span className="font-mono text-[13px]">{r.internal}</span> },
          { key: 'nd', label: 'Нода', render: (r) => <span className="text-[13px] text-neutral-700">{nodeMap[r.node_id] || r.node_id.slice(0, 8)}</span> },
          { key: 'c', label: 'Комментарий', render: (r) => <span className="text-[12px] text-neutral-500">{r.comment || '—'}</span> },
          { key: 'act', label: '', w: '1%', render: (r) => (
            <div className="flex justify-end">
              <RowMenu items={[
                { label: 'Удалить', danger: true,
                  onClick: () => confirm(`Удалить NAT ${r.external}→${r.internal}?`) && del.mutate(r.id) },
              ]} />
            </div>
          )},
        ]}
      />
      {addOpen && <AddNATModal nodes={nodes} onClose={() => setAddOpen(false)}
        onDone={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['nat-rules'] }) }} />}
    </>
  )
}

function AddNATModal({ nodes, onClose, onDone }: { nodes: Node[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ node_id: nodes[0]?.id || '', external: '', internal: '', comment: '' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/nat-rules', f)).data,
    onSuccess: onDone,
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Добавить NAT-правило">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Нода">
          <Select value={f.node_id} onChange={e => setF({ ...f, node_id: e.target.value })}>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
          </Select>
        </Field>
        <Field label="Внешний IP"><Input value={f.external} onChange={e => setF({ ...f, external: e.target.value })} placeholder="46.8.189.100" required className="font-mono" /></Field>
        <Field label="Внутренний IP"><Input value={f.internal} onChange={e => setF({ ...f, internal: e.target.value })} placeholder="10.0.0.5" required className="font-mono" /></Field>
        <Field label="Комментарий"><Input value={f.comment} onChange={e => setF({ ...f, comment: e.target.value })} /></Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-100 -mx-5 px-5 pt-4">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>{create.isPending ? '…' : 'Создать'}</Button>
        </div>
      </form>
    </Modal>
  )
}
